// V2 版式：quad-matrix（前端 DOM 版）——对照后端 v2/layouts/quad-matrix.ts 逐坐标移植
// N×M 等距格：title+desc+星（■ 方块 0-3）；stars>=3 格 accent 底（lint 保证最多 1 格）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { luxeExt } from "./luxe";
import { reportTheme, inkTheme, memphisTheme } from "./themes";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2QuadMatrix({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const GEO = V2GEO["quad-matrix"] as any;
  const m = SPACING.marginX;
  const W = 13.333;
  const mag = skin.style === "magazine";
  // 格底色：瑞士 grey1 / luxe 浅金 blush（quad-matrix.ts 分支），其余 skin.hairline
  const ext = luxeExt(skin);
  const theme = skin.theme as { grey?: { grey1?: string } };
  const grey1 = theme.grey?.grey1 ?? (ext ? ext.blush : skin.hairline);
  // 第二批皮肤（043 §4.5–4.7）：report 暗格 muted 细边/memphis 白底大圆角格
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);

  const bi = c.blocks.findIndex((x) => x.type === "gridmatrix");
  const b = bi >= 0 ? c.blocks[bi] : null;
  if (!b || b.type !== "gridmatrix") {
    return <V2Chrome skin={skin} kicker={kickerOf(page, "quad-matrix")} page={page} total={total} foot={footOf()} />;
  }

  const gridX = m + GEO.leftAxisW;
  const gridW = W - m - gridX;
  const gridH = GEO.gridBottom - GEO.gridTop;
  const cw = (gridW - (b.cols - 1) * GEO.cellGap) / b.cols;
  const ch = (gridH - (b.rows - 1) * GEO.cellGap) / b.rows;
  const metaStyle = { fontSize: pt(skin.scale.meta), color: skin.sub, fontFamily: skin.fontBody } as const;
  // 格内文字色按格底色取（浅格上必须用墨色，ink 底页 skin.text 是纸白会隐形——对齐后端 quad-matrix.ts 修复）
  const th = skin.theme as Record<string, unknown>;
  const cellFg = skin.style === "swiss" ? ((th.grey as Record<string, string>).ink) : (th.ink as string ?? skin.text);
  const cellFgSub = skin.style === "swiss" ? ((th.grey as Record<string, string>).grey3) : cellFg;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "quad-matrix")} page={page} total={total} foot={footOf()} />
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(GEO.titleY), width: px(W - 2 * m), height: px(GEO.titleH),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.1,
        }}
      />
      {/* 轴说明 */}
      <div style={{ position: "absolute", left: px(gridX), top: px(GEO.gridTop - 0.42), width: px(gridW), height: px(0.32), textAlign: "center", ...metaStyle }}>← {b.xAxis} →</div>
      <div style={{
        position: "absolute", left: px(m), top: px(GEO.gridTop), width: px(GEO.leftAxisW - 0.12), height: px(gridH),
        writingMode: "vertical-rl", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", ...metaStyle,
      }}>← {b.yAxis} →</div>
      {/* 格子底（report 暗格 muted 细边 / ink 黛灰细边 / memphis 白底大圆角——quad-matrix.ts:79-84） */}
      {Array.from({ length: b.rows }, (_, r) => Array.from({ length: b.cols }, (_, col) => (
        <div key={`bg${r}-${col}`} style={{
          position: "absolute",
          left: px(gridX + col * (cw + GEO.cellGap)), top: px(GEO.gridTop + r * (ch + GEO.cellGap)),
          width: px(cw), height: px(ch),
          background: reportT ? reportT.inkDeep : mpT ? "#FFFFFF" : grey1,
          border: reportT ? `0.7px solid ${reportT.muted}` : inkT ? `1px solid ${inkT.accentGrey}` : undefined,
          borderRadius: mpT ? px(skin.radius) : 0,
        }} />
      )))}
      {/* 格内容 */}
      {b.cells.map((cell, i) => {
        if (cell.row < 0 || cell.row >= b.rows || cell.col < 0 || cell.col >= b.cols) return null;
        const x = gridX + cell.col * (cw + GEO.cellGap);
        const y = GEO.gridTop + cell.row * (ch + GEO.cellGap);
        const hot = cell.stars >= 3;
        // ink（043 §4.6）：朱红禁满铺——hot 格暖纸底 + 朱红细边；report hot 格琥珀底（quad-matrix.ts:94-104）
        const fg = hot ? (inkT ? inkT.accent : skin.accentOn) : cellFg;
        const fgSub = hot ? (inkT ? inkT.accent : skin.accentOn) : cellFgSub;
        return (
          <div key={i}>
            {hot && (
              <div style={{
                position: "absolute", left: px(x), top: px(y), width: px(cw), height: px(ch),
                background: inkT ? inkT.paper : skin.accent,
                border: inkT ? `1px solid ${inkT.accent}` : undefined,
                borderRadius: mpT ? px(skin.radius) : 0,
              }} />
            )}
            <EditableText
              value={cell.title} path={`blocks.${bi}.cells.${i}.title`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x + GEO.padX), top: px(y + GEO.padTop),
                width: px(cw - 2 * GEO.padX), height: px(GEO.cellTitleH),
                fontSize: pt(skin.scale.sectionHead - 2), color: fg,
                fontFamily: skin.fontBody, fontWeight: 700,
              }}
            />
            <EditableText
              value={cell.desc} path={`blocks.${bi}.cells.${i}.desc`} editable={editable} multiline
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(x + GEO.padX), top: px(y + GEO.descY),
                width: px(cw - 2 * GEO.padX), height: px(ch - GEO.descY + GEO.starsY - 0.06),
                fontSize: pt(skin.scale.caption), color: fgSub,
                fontFamily: skin.fontBody, lineHeight: 1.25,
              }}
            />
            <div style={{
              position: "absolute", left: px(x + GEO.padX), top: px(y + ch + GEO.starsY),
              width: px(cw - 2 * GEO.padX), height: px(GEO.starsH),
              fontSize: pt(GEO.starSize), color: hot ? skin.accentOn : skin.accent,
              fontFamily: skin.fontBody, display: "flex", alignItems: "flex-end", letterSpacing: 2,
            }}>{(GEO.starGlyph as string).repeat(Math.max(0, Math.min(3, Math.round(cell.stars))))}</div>
          </div>
        );
      })}
    </>
  );
}
